import { useEffect } from 'react';
import { Box, Container, Typography, Grid, Stack } from '@mui/material';
import { CheckCircleOutlined as CheckIcon, VisibilityOutlined as VisibilityIcon } from '@mui/icons-material';
import { navigate } from '../../lib/navigation';
import { track } from '../../lib/analytics';
import Button from '../../components/Button';
import FloatingHelpButton from '../../components/FloatingHelpButton';
import InviteBanner from '../../components/InviteBanner';
import Navbar from '../../components/Navbar';
import CampaignCard from './CampaignCard';
import CreatorProfileCard from './CreatorProfileCard';
import FAQAccordion from './FAQAccordion';
import FeatureSection from './FeatureSection';
import Footer from './Footer';
import Hero from './Hero';
import { benefits, campaignExample, exampleCreator, faqs, howItWorksSteps } from './mockData';

function SectionHeading({ title, subtitle, dark = false, align = 'left' }) {
  return (
    <Box sx={{ mb: 5, textAlign: align, maxWidth: align === 'center' ? 640 : 'none', mx: align === 'center' ? 'auto' : 0 }}>
      <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' }, mb: subtitle ? 1.5 : 0 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="h6" fontWeight={400} sx={{ color: dark ? 'rgba(255,255,255,0.75)' : 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function LandingPage({ auth }) {
  const authed = auth?.status === 'authenticated';
  // Creator-specific landing links carry the invitation context; the generic
  // root landing page and broad Instagram traffic keep the standard presentation.
  const isTrackedLanding = window.location.pathname.startsWith('/landing/');
  const pathTracker = isTrackedLanding ? window.location.pathname.slice('/landing/'.length).split('/')[0].trim().toLowerCase() : '';
  const queryTracker = new URLSearchParams(window.location.search).get('tracker')?.trim().toLowerCase();
  const isInvite = isTrackedLanding && !['ig', 'business'].includes(pathTracker) && queryTracker !== 'ig';
  const goToSignup = (label = 'create_creatorcv') => {
    track('landing_cta_clicked', { path: window.location.pathname, label });
    navigate(authed ? '/dashboard' : '/signup');
  };

  const viewExample = (label) => {
    track('landing_cta_clicked', { path: window.location.pathname, label });
    navigate('/example');
  };

  const focusOpportunityForm = (label) => {
    track('landing_cta_clicked', { path: window.location.pathname, label });
    document.getElementById('ugc-opportunity-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => document.getElementById('ugc-opportunity-email')?.focus({ preventScroll: true }), 450);
  };

  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get('scrollTo');
    if (!target) return;
    requestAnimationFrame(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (target === 'ugc-opportunities') {
        window.setTimeout(() => document.getElementById('ugc-opportunity-email')?.focus({ preventScroll: true }), 450);
      }
    });
  }, []);

  useEffect(() => {
    const sectionIds = ['ugc-opportunities', 'creatorcv-profile', 'how-it-works', 'campaign-proof', 'benefits', 'faq', 'final-cta'];
    const seenSections = new Set();
    let observer;

    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || seenSections.has(entry.target.id)) return;
            seenSections.add(entry.target.id);
            track('landing_section_viewed', { path: window.location.pathname, label: entry.target.id });
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );
      sectionIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section) observer.observe(section);
      });
    }

    const seenDepths = new Set();
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percentage = scrollable <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      [25, 50, 75, 100].forEach((milestone) => {
        if (percentage < milestone || seenDepths.has(milestone)) return;
        seenDepths.add(milestone);
        track('landing_scroll_depth_reached', { path: window.location.pathname, label: `${milestone}_percent` });
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <>
      <Navbar authed={authed} />

      <Box component="main">

      {isInvite && <InviteBanner />}

      <Hero />

      {/* CreatorCV profile pitch */}
      <Box id="creatorcv-profile" sx={{ bgcolor: 'background.paper', scrollMarginTop: 72 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionHeading
                title="Build your CreatorCV before the right brief arrives."
                subtitle="Keep everything a brand needs to assess your fit in one profile, so you can respond quickly when a relevant UGC opportunity lands."
              />
              <Stack spacing={1.5} sx={{ mb: 3.5 }}>
                {['Your best UGC samples and past campaigns', 'Your niches, location, and audience insights', 'One professional link ready to share'].map((benefit) => (
                  <Stack key={benefit} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <CheckIcon color="primary" fontSize="small" />
                    <Typography fontWeight={600}>{benefit}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button onClick={() => goToSignup('profile_pitch_create')}>Create your CreatorCV</Button>
                <Button variant="secondary" startIcon={<VisibilityIcon />} onClick={() => viewExample('profile_pitch_example')}>View example profile</Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <CreatorProfileCard creator={exampleCreator} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Box id="how-it-works" sx={{ bgcolor: '#F7F5FC', scrollMarginTop: 72 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <SectionHeading title="From opportunity alert to brand-ready response." />
          <Grid container spacing={2.5}>
            {howItWorksSteps.map((step) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={step.step}>
                <FeatureSection icon={step.icon} step={step.step} title={step.title} description={step.description} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Showcase past work feature */}
      <Box id="campaign-proof" sx={{ bgcolor: 'background.paper', scrollMarginTop: 72 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={{ xs: 5, md: 7 }} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionHeading
                title="A stronger profile makes every opportunity easier to act on."
                subtitle="Add brand partnerships, gifted campaigns, UGC, and personal projects so brands can quickly understand what you create."
              />
              <Button onClick={() => goToSignup('campaign_proof_create')}>Create your CreatorCV</Button>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <CampaignCard campaign={campaignExample} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits section */}
      <Box sx={{ bgcolor: '#F7F5FC' }}>
        <Container id="benefits" maxWidth="lg" sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: 72 }}>
          <SectionHeading title="Built for creators at every stage, from your first paid campaign to your fiftieth." align="center" />
          <Grid container spacing={2.5}>
            {benefits.map((benefit) => (
              <Grid size={{ xs: 12, sm: 4 }} key={benefit.title}>
                <FeatureSection icon={benefit.icon} title={benefit.title} description={benefit.description} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Container id="faq" maxWidth="md" sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: 72 }}>
          <SectionHeading title="Frequently asked questions" align="center" />
          <FAQAccordion
            faqs={faqs}
            onToggle={(faq, opening) => {
              if (opening) track('landing_faq_opened', { path: window.location.pathname, label: faq.question });
            }}
          />
        </Container>
      </Box>

      {/* Final CTA */}
      <Box id="final-cta" sx={{ bgcolor: '#F7F5FC', scrollMarginTop: 72 }}>
        <Container maxWidth="md" sx={{ py: { xs: 7, md: 10 }, textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem' }, mb: 1.5 }}>
            Be first to hear about new UGC opportunities.
          </Typography>
          <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ mb: 3.5 }}>
            Join the opportunity list now. Build your CreatorCV when you’re ready to show brands what you can do.
          </Typography>
          <Button onClick={() => focusOpportunityForm('final_join_opportunity_list')} sx={{ mb: 1.5 }}>
            Join the UGC opportunity list
          </Button>
          <Typography variant="body2" color="text.secondary">
            Free to create. Built for emerging and seasoned creators.
          </Typography>
        </Container>
      </Box>

      </Box>
      <Footer />
      <FloatingHelpButton />
    </>
  );
}

export default LandingPage;
